import { defineStore } from "pinia";
import {
    FetchState,
    NotificationPreferencesState,
    NotificationPreferencesResponse,
    NotificationPreferenceUpdateRequest,
    NotificationPreference,
    NotificationPreferenceType, Preference,
} from "@/shared/types";
import {
    getNotificationPreferences,
    updateNotificationPreferences,
} from "@/shared/api/notificationPreferencesService";
import * as storeHelper from "@/shared/storeHelper";
import _ from "lodash";
import { useNotifierStore } from "./notifier";

export const initialState: NotificationPreferencesState = {
    notificationPreferencesFetchState: FetchState.Uninitialized,
    notificationPreferences: [],
    notificationPreferencesSnapshot: [],
    hasSavedNotificationPreferences: false,
};

export const useNotificationPreferencesStore = defineStore("notificationPreferences", {
    state: (): NotificationPreferencesState => (_.cloneDeep(initialState)),
    getters: {
        notificationPreferencesLoading: state => state.notificationPreferencesFetchState === FetchState.Loading,
        notificationPreferencesLoaded: state => state.notificationPreferencesFetchState === FetchState.Loaded,
        notificationPreferencesList: state => state.notificationPreferences,
    },
    actions: {
        setNotificationPreferences (response: NotificationPreferencesResponse) {
            this.notificationPreferences = this.normalizePreferences(response.notificationPreferences);
            this.notificationPreferencesSnapshot = _.cloneDeep(this.notificationPreferences);
        },
        setNotificationPreferencesFetchState (fetchState: FetchState) {
            this.notificationPreferencesFetchState = fetchState;
        },
        hasThresholdOption (preference: Preference): boolean {
            return !!(preference && (preference.notificationPreferenceType === NotificationPreferenceType.HsaWithdrawalWarning ||
                preference.notificationPreferenceType === NotificationPreferenceType.HsaCashBalanceWarning ||
                preference.notificationPreferenceType === NotificationPreferenceType.HsaCashMaximumBalanceWarning));
        },
        normalizePreferences (notificationPreferences: NotificationPreference[]): NotificationPreference[] {
            return notificationPreferences.map(notification => {
                return {
                    ...notification,
                    preferences: notification.preferences.map(pref => {
                        const hasThresholdOption = this.hasThresholdOption(pref);
                        if (!hasThresholdOption) {
                            return pref;
                        }
                        if (!pref.threshold) {
                            return {
                                ...pref,
                                threshold: {
                                    value: ""
                                }
                            };
                        }
                        if (pref.threshold && typeof pref.threshold === "object") {
                            const threshold = pref.threshold as { value?: number | null };

                            return {
                                ...pref,
                                threshold: {
                                    ...threshold,
                                    value: threshold.value ?? ""
                                }
                            };
                        }
                        return pref;
                    })
                };
            });
        },
        async getNotificationPreferences () {
            if (this.notificationPreferencesFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setNotificationPreferencesFetchState(FetchState.Loading);
            try {
                const response = await getNotificationPreferences();
                this.setNotificationPreferencesFetchState(FetchState.Loaded);
                this.setNotificationPreferences(response.data);
            } catch (err) {
                this.setNotificationPreferencesFetchState(FetchState.Failed);
                storeHelper.handleError({ error: err, rethrow: true, timeout: 5000 });
            }
        },
        async updateNotificationPreferences (request: NotificationPreferenceUpdateRequest) {
            if (this.notificationPreferencesFetchState !== FetchState.Loaded) {
                return Promise.resolve();
            }
            const notifierStore = useNotifierStore();
            try {
                this.hasSavedNotificationPreferences = false;
                const response = await updateNotificationPreferences(request);
                this.hasSavedNotificationPreferences = true;
                this.setNotificationPreferences(response);
                notifierStore.setSuccess("Communication preferences updated successfully.", notifierStore.shortTimeout);
            } catch (err) {
                const errorMessage = "Sorry, we are unable to update your communication preferences. Please try again later.";

                storeHelper.handleError({
                    error: err,
                    rethrow: false,
                    timeout: 5000,
                    overrideNotificationMessage: errorMessage
                });
                this.setNotificationPreferencesFetchState(FetchState.Failed);
                this.notificationPreferences = _.cloneDeep(this.notificationPreferencesSnapshot);
                window.setTimeout(() => {
                    this.setNotificationPreferencesFetchState(FetchState.Loaded);
                }, 0);
            }
        },
    }
});